/**
 * Diamond360 custom element — frame-sequence 360° viewer for diamonds.
 *
 * Ports the legacy `video_player_diamond_2018.js` spinner from the pre-Shopify
 * platform. See docs/diamond-360-viewer.md for the reverse-engineering notes.
 *
 * The DOM mirrors the legacy `#azPlayer` exactly, because the shape of it is
 * load-bearing:
 *
 *   <picture><img data-frame></picture>   one visible frame; playback swaps its src
 *   <div data-progress>                   "NN%" while the sequence loads
 *   <div data-preload>                    every frame as a hidden <img>
 *
 * The hidden preload images must stay *attached to the document*. That is what
 * pins each decoded frame in the image cache, so swapping `src` on the visible
 * <img> is a cache hit rather than a refetch. Detached `new Image()` objects
 * are collectable and a `loading="lazy"` display element re-resolves on every
 * swap — both cause the viewer to re-request frames it has already fetched.
 *
 * @example
 * <diamond-360
 *   base="https://images.allurez.com/gemsimages/magicvideos"
 *   sequence="emerald"
 *   frames="374"></diamond-360>
 *
 * @module diamond-360
 * @version 2.0.0
 * @extends HTMLElement
 */
class Diamond360 extends HTMLElement {

  /**
   * @static {number} One full revolution, in milliseconds. Tuned by eye. With
   * the full 374-frame sequence this is ~64ms per frame (~16fps), so the speed
   * is set purely on how it looks — smoothness does not trade against it.
   */
  static ROTATION_MS = 24000;

  /** @static {number} Fraction of frames that must be decoded before autoplay starts. */
  static PLAY_THRESHOLD = 0.25;

  /** @static {number} Fewest frames worth rendering a spin from. */
  static MIN_FRAMES = 12;

  /** @static {string} Pause glyph for the playback toggle. */
  static ICON_PAUSE = '<svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><rect x="2" y="1.5" width="3" height="9" rx="0.5"/><rect x="7" y="1.5" width="3" height="9" rx="0.5"/></svg>';

  /** @static {string} Play glyph for the playback toggle. */
  static ICON_PLAY = '<svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><path d="M3 1.5v9l7.5-4.5z"/></svg>';


  /** @static {string} id of the shared toggle stylesheet, injected once per page. */
  static TOGGLE_STYLE_ID = 'stone-360-toggle-styles';

  /** @static {number} A press that travels less than this (px) is a tap, not a drag. */
  static TAP_MAX_PX = 6;

  /**
   * Inject the toggle styles once. Shared with the other 360 player — whichever
   * connects first wins, the other finds the tag already there.
   *
   * The button sits just below the stone, centred on the stage's bottom edge so
   * it never covers the diamond, hidden until the pointer hovers the stage (or
   * it receives keyboard focus), and only on laptop+ — on mobile the gallery
   * carousel handles the slide and there is no hover.
   */
  static ensureToggleStyles() {

    if (document.getElementById(this.TOGGLE_STYLE_ID)) {
      return;
    }

    const style = document.createElement('style');
    style.id = this.TOGGLE_STYLE_ID;
    style.textContent = `
      .stone-360-toggle {
        position: absolute;
        bottom: 12px;
        left: 50%;
        width: 36px;
        height: 36px;
        margin: 0 0 0 -18px;
        display: none;
        align-items: center;
        justify-content: center;
        border: 0;
        padding: 0;
        border-radius: 9999px;
        background: rgba(255, 255, 255, 0.92);
        color: #231F20;
        box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.15s ease;
        z-index: 10;
      }
      @media (min-width: 1024px) {
        .stone-360-toggle { display: flex; }
      }
      .stone-360-stage:hover .stone-360-toggle,
      .stone-360-toggle:focus-visible {
        opacity: 1;
      }
    `;

    document.head.appendChild(style);
  }

  constructor() {

    super();

    /** @private {string[]} Frame URLs, in rotation order */
    this.urls = [];

    /**
     * @private {HTMLImageElement[]} The hidden preload images, kept attached to
     * the document for the life of the viewer so each frame is fetched once.
     */
    this.preloaded = [];

    /** @private {Set<number>} Indices of frames that have finished decoding */
    this.loaded = new Set();

    /** @private {number} Index of the frame currently displayed */
    this.index = 0;

    /**
     * @private {number} Playback direction through `urls`. 1 walks the sequence
     * from the first frame to the last (0 -> 374), which is the direction the
     * stone should turn.
     */
    this.direction = 1;

    /** @private {number|null} Autoplay interval handle */
    this.timer = null;

    /** @private {boolean} Whether frame loading has been kicked off */
    this.activated = false;

    /** @private {number|null} Active drag pointer */
    this.pointerId = null;

    /** @private {number} Last pointer X during a drag */
    this.pointerX = 0;

    /** @private {number} Sub-frame drag remainder, so slow drags still move */
    this.dragRemainder = 0;

    /**
     * @private {boolean} Whether the visitor paused the spin with the toggle.
     * A deliberate pause survives drags, which otherwise resume playback.
     */
    this.userPaused = false;

    /** @private {boolean} Whether the current press started on the toggle */
    this.pressedToggle = false;

    /** @private {number} X where the current press started, for tap detection */
    this.pressStartX = 0;

    /** @private {number} Furthest horizontal travel of the current press */
    this.pressTravel = 0;

    /** @private {boolean} Whether the spin was running when the press started */
    this.wasPlaying = false;
  }

  connectedCallback() {

    this.base = (this.getAttribute('base') || '').replace(/\/+$/, '');
    this.sequence = (this.getAttribute('sequence') || '').replace(/^\/+|\/+$/g, '');
    this.totalFrames = parseInt(this.getAttribute('frames'), 10) || 0;
    this.label = this.getAttribute('alt') || 'Diamond 360° view';

    if (!this.base || !this.sequence || this.totalFrames < Diamond360.MIN_FRAMES) {
      this.removeSlide();
      return;
    }

    this.urls = this.buildFrameUrls();

    this.render();
    this.initEvents();
    this.autoActivate();
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
  }

  /**
   * Start the spin on its own. Gated on visibility so a slide sitting off-screen
   * (mobile carousel, below the fold) doesn't pull the whole sequence down
   * before anyone can see it — in practice this is the first gallery slide, so
   * it fires immediately.
   *
   * @private
   */
  autoActivate() {

    if (typeof IntersectionObserver === 'undefined') {
      this.activate();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.observer.disconnect();
        this.activate();
      }
    });

    this.observer.observe(this);
  }

  /**
   * Build the list of frame URLs.
   *
   * The legacy asset naming pads to two digits for frames 1-9 only, so the
   * sequence runs `_01 … _09, _10 … _374`. `_00` exists on the CDN but the
   * legacy player never used it, and neither do we.
   *
   * `frame-step` thins the sequence: 1 (the default) plays every frame exactly
   * like the legacy player, higher values trade smoothness for payload.
   *
   * @returns {string[]} Frame URLs in rotation order
   * @private
   */
  buildFrameUrls() {

    const step = Math.max(1, parseInt(this.getAttribute('frame-step'), 10) || 1);
    const count = step === 1
      ? this.totalFrames
      : Math.max(Diamond360.MIN_FRAMES, Math.round(this.totalFrames / step));

    // The sequence path may be nested ("NVD/ABC123"); the file prefix is the last segment.
    const prefix = this.sequence.split('/').pop();

    return Array.from({ length: count }, (_, i) => {
      // Spread evenly across the full revolution so the loop stays seamless.
      const frame = step === 1 ? i + 1 : Math.round((i * this.totalFrames) / count) + 1;
      const padded = frame < 10 ? `0${frame}` : `${frame}`;

      return `${this.base}/${this.sequence}/${prefix}_${padded}.jpg`;
    });
  }

  render() {

    // Opt the slide out of Swiper — every drag on the viewer rotates the
    // stone; the gallery's arrows are the way off this slide (same gesture
    // design as <video-360>). Swiper resolves the class with closest() from
    // the event target, so it must sit on an ancestor of everything draggable.
    this.classList.add('swiper-no-swiping');

    Diamond360.ensureToggleStyles();

    this.innerHTML = `
      <div class="relative w-full select-none swiper-no-swiping stone-360-stage" data-stage>

        <picture>
          <img
            data-frame
            src="${ this.urls[0] }"
            alt="${ this.label }"
            width="600"
            height="600"
            draggable="false"
            class="w-full h-auto swiper-no-swiping" />
        </picture>

        <div
          data-progress
          class="absolute inset-0 hidden items-center justify-center text-xs text-primary-300"
          role="status"
          aria-live="polite"></div>

        <div data-preload hidden></div>

        <button
          type="button"
          data-toggle
          class="stone-360-toggle"
          aria-label="Pause 360° spin"></button>

      </div>
    `;

    this.stage = this.querySelector('[data-stage]');
    this.frameEl = this.querySelector('[data-frame]');
    this.progressEl = this.querySelector('[data-progress]');
    this.preloadEl = this.querySelector('[data-preload]');
    this.toggleEl = this.querySelector('[data-toggle]');

    this.updateToggle();

    // Vertical page scrolling must still work when a touch starts on the viewer.
    this.stage.style.touchAction = 'pan-y';
    this.stage.style.cursor = 'w-resize';
  }

  initEvents() {

    // A missing first frame means the sequence isn't on the CDN for this shape.
    this.frameEl.addEventListener('error', () => this.removeSlide(), { once: true });

    this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.stage.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.stage.addEventListener('pointercancel', this.onPointerUp.bind(this));

    this.stage.addEventListener('dragstart', (event) => event.preventDefault());

    // Pointer presses on the toggle are resolved in onPointerUp (tap vs drag),
    // so the click handler only serves keyboard activation (detail === 0).
    this.toggleEl.addEventListener('click', (event) => {
      if (event.detail === 0) {
        this.toggle();
      }
    });
  }

  /**
   * Preload the whole sequence into hidden, attached <img> elements — the
   * legacy `Player.Preload`. Playback starts as soon as enough frames are
   * decoded rather than waiting for the whole set.
   *
   * @private
   */
  activate() {

    if (this.activated) {
      return;
    }

    this.activated = true;

    this.progressEl.classList.remove('hidden');
    this.progressEl.classList.add('flex');
    this.progressEl.textContent = '1%';

    const threshold = Math.ceil(this.urls.length * Diamond360.PLAY_THRESHOLD);
    const fragment = document.createDocumentFragment();

    this.urls.forEach((url, i) => {

      const image = new Image();

      const onSettled = () => {

        this.loaded.add(i);

        const percent = Math.round((this.loaded.size / this.urls.length) * 100);
        this.progressEl.textContent = `${ percent }%`;

        if (this.loaded.size >= threshold) {
          this.progressEl.classList.add('hidden');
          this.progressEl.classList.remove('flex');
          this.play();
        }
      };

      // A single missing frame shouldn't stall the spin — drop it and move on.
      image.addEventListener('error', () => {
        this.urls[i] = null;
        onSettled();
      }, { once: true });

      image.addEventListener('load', () => {
        // decode() keeps the first paint of each frame off the main thread.
        if (typeof image.decode === 'function') {
          image.decode().then(onSettled, onSettled);
        } else {
          onSettled();
        }
      }, { once: true });

      image.src = url;
      image.style.display = 'none';

      this.preloaded[i] = image;
      fragment.appendChild(image);
    });

    // Attaching is what keeps the decoded frames alive; see the class comment.
    this.preloadEl.appendChild(fragment);
  }

  /**
   * Start autoplay. Skipped when the visitor paused the spin themselves or
   * prefers reduced motion — unless they explicitly asked for it via the toggle.
   *
   * @param {boolean} [explicit=false] - Whether the visitor pressed play
   */
  play(explicit = false) {

    if (this.timer) {
      return;
    }

    if (!explicit && this.userPaused) {
      return;
    }

    if (!explicit && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const interval = Diamond360.ROTATION_MS / this.urls.length;

    this.timer = setInterval(() => this.step(this.direction), interval);
    this.updateToggle();
  }

  /** Halt autoplay. */
  stop() {

    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    this.updateToggle();
  }

  /**
   * Flip between spinning and paused from the playback toggle.
   *
   * @param {boolean} [playing] - State to flip from; defaults to the live one.
   *   A press stops the spin before it resolves into a tap, so the tap passes
   *   the state from before the press.
   */
  toggle(playing = Boolean(this.timer)) {

    if (playing) {
      this.userPaused = true;
      this.stop();
    } else {
      this.userPaused = false;
      this.play(true);
    }
  }

  /**
   * Reflect the playback state on the toggle button.
   * @private
   */
  updateToggle() {

    if (!this.toggleEl) {
      return;
    }

    const playing = Boolean(this.timer);

    this.toggleEl.innerHTML = playing ? Diamond360.ICON_PAUSE : Diamond360.ICON_PLAY;
    this.toggleEl.setAttribute('aria-label', playing ? 'Pause 360° spin' : 'Play 360° spin');
  }

  /**
   * Advance by `delta` frames, wrapping around and skipping frames that haven't
   * loaded yet (or 404'd) so the spin never lands on a blank.
   *
   * @param {number} delta - Signed number of frames to move
   * @private
   */
  step(delta) {

    if (!this.loaded.size || !delta) {
      return;
    }

    const total = this.urls.length;
    const stride = delta > 0 ? 1 : -1;
    let next = this.index;

    for (let moved = 0; moved < Math.abs(delta);) {
      next = (next + stride + total) % total;

      if (this.loaded.has(next) && this.urls[next]) {
        moved++;
      } else if (next === this.index) {
        // Wrapped all the way around without finding a loadable frame.
        return;
      }
    }

    this.index = next;

    // Cache hit: the frame is pinned by its hidden preload <img>.
    this.frameEl.src = this.urls[next];
  }

  onPointerDown(event) {

    if (!this.activated) {
      return;
    }

    this.pointerId = event.pointerId;
    this.pointerX = event.clientX;
    this.dragRemainder = 0;

    // A press on the toggle may still become a drag — the button sits where
    // people naturally grab the stone — so it is resolved on release.
    this.pressedToggle = Boolean(event.target.closest('[data-toggle]'));
    this.pressStartX = event.clientX;
    this.pressTravel = 0;
    this.wasPlaying = Boolean(this.timer);

    this.stage.setPointerCapture(event.pointerId);
    this.stop();
  }

  onPointerMove(event) {

    if (this.pointerId !== event.pointerId) {
      return;
    }

    this.pressTravel = Math.max(this.pressTravel, Math.abs(event.clientX - this.pressStartX));

    // A drag across the full width of the viewer is one full revolution.
    const pixelsPerFrame = this.stage.getBoundingClientRect().width / this.urls.length;
    const travelled = event.clientX - this.pointerX + this.dragRemainder;
    const frames = Math.trunc(travelled / pixelsPerFrame);

    this.pointerX = event.clientX;
    this.dragRemainder = travelled - frames * pixelsPerFrame;

    if (frames) {
      this.direction = frames > 0 ? 1 : -1;
      this.step(frames);
    }
  }

  onPointerUp(event) {

    if (this.pointerId !== event.pointerId) {
      return;
    }

    if (this.stage.hasPointerCapture(event.pointerId)) {
      this.stage.releasePointerCapture(event.pointerId);
    }

    this.pointerId = null;

    // A press on the toggle that didn't travel is a tap: flip playback from
    // the state before the press (the press itself stopped the spin).
    if (this.pressedToggle && this.pressTravel < Diamond360.TAP_MAX_PX) {
      this.pressedToggle = false;
      this.toggle(this.wasPlaying);
      return;
    }

    this.pressedToggle = false;

    // Dragging resumes playback, matching the legacy player — unless the
    // visitor paused the spin on purpose.
    this.play();
  }

  /**
   * Take the viewer out of the page entirely. Used when the sequence turns out
   * not to exist on the CDN — the gallery should fall back to its other slides
   * rather than show a broken frame.
   *
   * @private
   */
  removeSlide() {
    this.stop();
    (this.closest('.swiper-slide') || this).remove();
  }
}

customElements.define('diamond-360', Diamond360);
